<template>
  <div class="product-select-section">
    <template v-if="allProducts.length > 0">
      <div class="booking-divider-title text-header-color w-100">What service would you like for {{petName}}?</div>
      <div class="timeslots">
        <validation-message v-if="message" :message="message" class="mb-2"/>
        <div v-for="product in allProducts" :key="product.id" @click="handleProductSelect(product)" class="btn-product" :class="{'btn-product-active' : productIds.includes(product.id)}">
          <div class="btn-product-name" :class="{ 'my-2': !siteBookingSettingDisplayServicePrices }">
            {{ product.name }}
          </div>
          <div v-if="siteBookingSettingDisplayBookingDuration && siteBookingSettingServiceBasedDurationEnabled && product.durationMinutes > 0" class="btn-product-duration">
            Duration: {{ formatDuration(product.durationMinutes) }}
          </div>
          <!-- Description with see more toggle -->
          <div v-if="siteBookingSettingDisplayServiceDescriptions && product.description" class="btn-product-description">
            <span>
              {{ isExpanded(product.id) ? product.description : truncated(product.description) }}
            </span>
            <b v-if="product.description.length > maxLength" @click.stop="toggleExpand(product.id)"  class="cursor-pointer ml-1">
              {{ isExpanded(product.id) ? 'See less' : 'See more' }}
            </b>
          </div>
          <div v-if="siteBookingSettingDisplayServicePrices" class="btn-product-price">
            {{ currencySymbol }}{{ product.amount.toFixed(2).toString() }}
          </div>
        </div>
      </div>
    </template>
    <div v-else-if="noProducts" class="font-size-h2 text-header-color mt-3 mt-md-1 px-4 text-center">NO SERVICES AVAILABLE</div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { formatDuration } from '@/helpers/DurationHelper'
import { ServiceProductCacheMixin } from '@/mixins/Booking/ServiceProductCacheMixin'
import { BreedProductCacheMixin } from '@/mixins/Booking/BreedProductCacheMixin'
import { PetProductCacheMixin } from '@/mixins/Booking/PetProductCacheMixin'
import { CountryCacheMixin } from '@/mixins/Booking/CountryCacheMixin'
import { SiteBookingSettingMixin } from '@/mixins/Booking/SiteBookingSettingMixin'
import { PetDurationCacheMixin } from '@/mixins/Booking/PetDurationCacheMixin'
import ValidationMessage from '@/components/ErrorValidation/ValidationMessage.vue'

export default {
  name: 'ProductsSelectItem',
  mixins: [SiteBookingSettingMixin, ServiceProductCacheMixin, BreedProductCacheMixin, PetProductCacheMixin, CountryCacheMixin, PetDurationCacheMixin],
  props: {
    id: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      message: '',
      isValid: false,
      expanded: [],
      maxLength: 100
    }
  },
  computed: {
    ...mapState('BookingUpload', [
      'selectedPets',
      'defaultDuration',
      'extendBeyondSlotDuration',
      'openSlotBooking',
      'openSlotDuration'
    ]),
    pet () {
      return this.selectedPets.length > 0 ? this.selectedPets.find(p => p.id === this.id) : null
    },
    breedId () {
      return this.pet ? this.pet.breedId : ''
    },
    petId () {
      return this.pet ? this.pet.id : ''
    },
    petName () {
      return this.pet ? this.pet.name : ''
    },
    serviceItems () {
      return this.pet ? this.pet.serviceItems : []
    },
    totalServiceItemsDuration () {
      return this.serviceItems?.reduce((sum, item) => sum + (item.durationMinutes || 0), 0) || 0
    },
    productIds () {
      return this.serviceItems.length > 0 ? this.serviceItems.map(p => p.id) : []
    },
    // pricingTypeOptions:
    // 1 = Published Services Only
    // 2 = Pet Profile Price + Published Services
    // 3 = Breed Price + Published Services
    // 4 = Auto
    // 5 = Pet Profile Price Only
    basicProductWithDuration () {
      if (this.clientTypeId === 1) {
        if ([2, 5].includes(this.siteBookingSettingPricingType)) {
          if (!this.petProduct) return null
          const duration = this.petProfileDurationMinutes || this.defaultDuration
          return Object.assign({}, this.petProduct, { durationMinutes: duration })
        }
        if (this.siteBookingSettingPricingType === 3) {
          if (!this.breedProduct) return null
          const duration = this.breedProduct.durationMinutes || this.defaultDuration
          return Object.assign({}, this.breedProduct, { durationMinutes: duration })
        }
        if (this.siteBookingSettingPricingType === 4) {
          if (this.petProduct) {
            const duration = this.petProfileDurationMinutes || this.defaultDuration
            return Object.assign({}, this.petProduct, { durationMinutes: duration })
          }
          if (this.breedProduct) {
            const duration = this.breedProduct.durationMinutes || this.defaultDuration
            return Object.assign({}, this.breedProduct, { durationMinutes: duration })
          }
          return null
        }
      }
      if (this.clientTypeId === 2) {
        if ([3, 4].includes(this.siteBookingSettingPricingType)) {
          if (!this.breedProduct) return null
          const duration = this.breedProduct.durationMinutes || this.defaultDuration
          return Object.assign({}, this.breedProduct, { durationMinutes: duration })
        }
        return null
      }
      return null
    },
    petProfileDuration () {
      return this.petDurations.find(d => d.petId === this.petId)
    },
    petProfileDurationMinutes () {
      const duration = this.petProfileDuration
      return duration ? duration.hours * 60 + duration.minutes : 0
    },
    serviceProductsWithDuration () {
      return this.serviceProducts.map(p => p.durationMinutes === 0 ? Object.assign({}, p, { durationMinutes: this.defaultDuration }) : p)
    },
    allProducts () {
      return (this.basicProductWithDuration ? [this.basicProductWithDuration] : []).concat(this.serviceProductsWithDuration)
    },
    noProducts () {
      return this.allProducts.length < 1 && (!this.loadingServiceProducts || !this.loadingBreedProduct || !this.loadingPetProduct)
    },
    fixedPetAppointmentDuration () {
      return this.petProfileDurationMinutes > 0 ? this.petProfileDurationMinutes : this.defaultDuration
    },
    petAppointmentDuration () {
      return this.siteBookingSettingServiceBasedDurationEnabled ? (this.totalServiceItemsDuration > 0 ? this.totalServiceItemsDuration : this.defaultDuration) : this.fixedPetAppointmentDuration
    },
    exceedsOpenSlotDuration () {
      return this.openSlotBooking && !this.extendBeyondSlotDuration && this.totalServiceItemsDuration > this.openSlotDuration
    }
  },
  watch: {
    petAppointmentDuration () {
      this.updatePetAppointmentDuration({ petId: this.petId, duration: this.petAppointmentDuration })
    },
    pet: {
      immediate: false,
      deep: true,
      handler () {
        this.updatePetAppointmentDuration({ petId: this.petId, duration: this.petAppointmentDuration })
      }
    },
    totalServiceItemsDuration () {
      this.check()
    }
  },
  methods: {
    formatDuration,
    truncated (text) {
      return text.length > this.maxLength ? text.slice(0, this.maxLength) + '...' : text
    },
    toggleExpand (id) {
      this.expanded.includes(id) ? this.expanded = this.expanded.filter(e => e !== id) : this.expanded.push(id)
    },
    isExpanded (id) {
      return this.expanded.includes(id)
    },
    ...mapMutations('BookingUpload', [
      'addServiceItem',
      'removeServiceItem',
      'clearServiceItems',
      'updatePetAppointmentDuration'
    ]),
    handleProductSelect (product) {
      if (this.allowedMultipleServiceSelect) {
        const existing = this.serviceItems.find(si => si.id === product.id)
        if (existing) {
          this.removeServiceItem({ petId: this.id, serviceItem: product })
        } else {
          if (this.serviceItems.length < this.siteBookingSettingMaxServices) {
            this.addServiceItem({ petId: this.id, serviceItem: product })
          }
        }
      } else {
        this.clearServiceItems(this.id)
        this.addServiceItem({ petId: this.id, serviceItem: product })
      }
    },
    back () {
      this.$emit('back')
    },
    next () {
      this.check()
      if (this.isValid) {
        this.$emit('next')
      }
    },
    check () {
      this.isValid = this.serviceItems.length > 0
      this.message = this.isValid ? '' : 'Please select a product'
      if (!this.isValid) return
      // exceeds set to isValid to false
      this.isValid = !this.exceedsOpenSlotDuration
      this.message = this.isValid ? '' : `Services exceed slot duration of ${formatDuration(this.openSlotDuration)}`
    },
    refreshServiceProducts () {
      // do nothing
    },
    refreshBreedProduct () {
      // do nothing
    },
    refreshPetProduct () {
      // do nothing
    }
  },
  components: {
    'validation-message': ValidationMessage
  }
}
</script>

<style scoped>
.booking-divider-title {
  @media screen and (min-width: 768px)  {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.product-select-section {
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media screen and (max-width: 768px)  {
    min-height: 10vh
  }
}

.timeslots {
  @media screen and (min-width: 768px)  {
    height: 100%;
    max-height: calc(650px - 20rem);
  }
}
</style>
